if (!window.AddonSharedServices) {
  class AddonSharedServices {
    constructor() {
      this.cache = new AddonCacheManager();
      this.sharedCart = new SharedCartService();
      this.price = document.querySelector('product-info[data-price]')?.dataset.price;
      this.buttonController = new AddonButtonController(this.cache, this.sharedCart, this.price);
      this.cache.cleanupExpiredCaches();
    }
  }
  
  class AddonCacheManager {
    constructor() {
      this.CACHE_EXPIRY = 24 * 60 * 60 * 1000;
    }
    
    generateUniqueId(mainProductId) {
      return `addon-${mainProductId}-${Date.now()}`;
    }
    
    getCache(mainProductId) {
      const key = `addon-state-${mainProductId}`;
      const cached = localStorage.getItem(key);
      
      if (!cached) return {};
      
      try {
        const data = JSON.parse(cached);
        
        if (data.timestamp && (Date.now() - data.timestamp) > this.CACHE_EXPIRY) {
          localStorage.removeItem(key);
          return {};
        }
        
        return data;
      } catch (error) {
        localStorage.removeItem(key);
        return {};
      }
    }
    
    updateAddon(mainProductId, addonType, variantId) {
      const cache = this.getCache(mainProductId);
      if (!cache.uniqueId) {
        cache.uniqueId = this.generateUniqueId(mainProductId);
        cache.mainProductId = mainProductId;
        cache.addons = {};
        cache.timestamp = Date.now();
      }
      
      cache.addons[addonType] = {
        variantId: variantId,
        timestamp: Date.now()
      };
      
      localStorage.setItem(`addon-state-${mainProductId}`, JSON.stringify(cache));
      return cache.uniqueId;
    }
    
    removeAddon(mainProductId, addonType) {
      const cache = this.getCache(mainProductId);
      if (cache.addons) {
        delete cache.addons[addonType];
        
        if (Object.keys(cache.addons).length === 0) {
          localStorage.removeItem(`addon-state-${mainProductId}`);
          return null;
        }
        
        localStorage.setItem(`addon-state-${mainProductId}`, JSON.stringify(cache));
      }
      return cache.uniqueId;
    }
    
    clearCache(mainProductId) {
      localStorage.removeItem(`addon-state-${mainProductId}`);
    }
    
    syncWithCart(mainProductId, cartItems) {
      const cache = this.getCache(mainProductId);
      if (!cache.addons) return;
      
      const cartVariantIds = new Set(cartItems.map(item => item.variant_id.toString()));
      let hasChanges = false;
      
      Object.keys(cache.addons).forEach(addonType => {
        const addon = cache.addons[addonType];
        if (!cartVariantIds.has(addon.variantId)) {
          delete cache.addons[addonType];
          hasChanges = true;
        }
      });
      
      if (hasChanges) {
        if (Object.keys(cache.addons).length === 0) {
          localStorage.removeItem(`addon-state-${mainProductId}`);
        } else {
          localStorage.setItem(`addon-state-${mainProductId}`, JSON.stringify(cache));
        }
      }
    }
    
    cleanupExpiredCaches() {
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('addon-state-')) {
          const cache = this.getCache(key.replace('addon-state-', ''));
          if (!cache.timestamp) {
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }
  
  class SharedCartService {
    constructor() {
      this.cartPromise = null;
      this.lastFetch = 0;
      this.CACHE_DURATION = 5000;
    }
    
    async getCart(forceRefresh = false) {
      const now = Date.now();
      
      if (!forceRefresh && this.cartPromise && (now - this.lastFetch) < this.CACHE_DURATION) {
        return this.cartPromise;
      }
      this.cartPromise = fetch('/cart.js').then(r => r.json());
      this.lastFetch = now;
      
      return this.cartPromise;
    }
    
    clearCache() {
      this.cartPromise = null;
      this.lastFetch = 0;
    }
  }
  
  class AddonButtonController {
    constructor(cacheManager, sharedCartService, price) {
      this.cache = cacheManager;
      this.sharedCart = sharedCartService;
      this.price = price;
      this.mainProductButton = null;
      this.originalButtonText = this.price ? `${this.price} - ADD TO BASKET` : 'ADD TO BASKET';
      
      this.setupEventListeners();
      
      // Update button text when DOM is ready
      document.addEventListener('DOMContentLoaded', () => this.updateButtonText());
    }
    
    setupEventListeners() {
      document.addEventListener('addon:added', () => this.updateButtonText());
      document.addEventListener('addon:removed', () => this.updateButtonText());
      document.addEventListener('addon:updated', () => this.updateButtonText());
      
      if (typeof subscribe === 'function') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'addon-system') return;
          this.updateButtonText();
        });
      }
      
      // Listen for product swap to re-intercept the form
      document.addEventListener('product-info:loaded', () => {
        this.interceptMainProductForm();
        this.updateButtonText();
      });
      
      this.interceptMainProductForm();
    }
    
    async updateButtonText() {
      if (!this.mainProductButton) {
        this.mainProductButton = document.querySelector('product-form .product-form__submit');
      }
      
      if (!this.mainProductButton) return;
      
      const buttonText = this.mainProductButton.querySelector('.button-text-wrapper') || this.mainProductButton.querySelector('span') || this.mainProductButton;
      const hasAnyAddons = await this.hasAnyAddonsInCache();
      const isMobile = window.innerWidth <= 749;
      let newText;
      
      if (this.price && !isMobile) {
        newText = hasAnyAddons ? `${this.price} - ADD SET TO BASKET` : this.originalButtonText;
      } else {
        newText = hasAnyAddons ? `ADD SET TO BASKET` : 'ADD TO BASKET';
      }
      buttonText.textContent = newText;
    }
    
    async hasAnyAddonsInCache() {
      const currentProductId = document.querySelector('product-info[data-product-id]')?.dataset.productId;
      if (!currentProductId) return false;
      
      const key = `addon-state-${currentProductId}`;
      const cache = JSON.parse(localStorage.getItem(key) || '{}');
      if (!cache.uniqueId) return false;
      
      try {
        const cart = await this.sharedCart.getCart();
        const hasAddonItems = cart.items.some(item => 
          item.properties?.addon_unique_id === cache.uniqueId
        );
        
        if (!hasAddonItems && cache.addons && Object.keys(cache.addons).length > 0) {
          localStorage.removeItem(key);
        }
        
        return hasAddonItems;
      } catch (error) {
        return cache.addons && Object.keys(cache.addons).length > 0;
      }
    }
    
    interceptMainProductForm() {
      const productFormElement = document.querySelector('product-form');
      const productForm = productFormElement?.querySelector('form[data-type="add-to-cart-form"]');
      if (!productForm || productForm.hasAttribute('data-addons-intercepted')) return;
  
      const originalOnSubmitHandler = productFormElement.onSubmitHandler.bind(productFormElement);
      
      // Reset button reference and update price since the form might be new
      this.mainProductButton = null;
      this.price = document.querySelector('product-info[data-price]')?.dataset.price;
      this.originalButtonText = this.price ? `${this.price} - ADD TO BASKET` : 'ADD TO BASKET';
        
      productForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
          
        if (await this.hasAnyAddonsInCache()) {
          await this.handleSetSubmission();
        } else {
          originalOnSubmitHandler(event);
        }
      }, true);
        
      productForm.setAttribute('data-addons-intercepted', 'true');
    }
    
    async handleSetSubmission() {
      const mainProductButton = this.mainProductButton;
      
      try {
        if (mainProductButton) {
          this.setMainProductButtonLoading(mainProductButton, true);
        }
        
        // Get current product ID
        const currentProductId = document.querySelector('product-info[data-product-id]')?.dataset.productId;
        if (!currentProductId) {
          throw new Error('Current product ID not found');
        }
        
        // Get main product variant ID
        const productForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
        const formData = new FormData(productForm);
        const mainVariantId = formData.get('id');
        
        if (!mainVariantId) {
          throw new Error('Main product variant ID not found');
        }
        
        // Get addons for ONLY the current product
        const cacheKey = `addon-state-${currentProductId}`;
        const cache = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        
        if (!cache.addons || Object.keys(cache.addons).length === 0) {
          throw new Error('No addons found for current product');
        }
        
        const allAddonItems = [];
        Object.values(cache.addons).forEach(addon => {
          allAddonItems.push({
            id: addon.variantId,
            quantity: 1,
            properties: {
              addon_unique_id: cache.uniqueId
            }
          });
        });
        
        // Get main product tags and calculate delivery cost
        const productInfo = document.querySelector('product-info[data-product-tags]');
        const productTags = productInfo?.dataset.productTags;
        const lowestDeliveryCost = productTags && typeof delivery_tabs !== 'undefined' ? 
          delivery_tabs.calculateLowestDeliveryCost(productTags) : null;
        
        const mainProductProperties = {};
        if (lowestDeliveryCost !== null) {
          mainProductProperties['Lowest Delivery Cost'] = String(lowestDeliveryCost);
        }

        const items = [{ 
          id: mainVariantId, 
          quantity: 1,
          properties: Object.keys(mainProductProperties).length > 0 ? mainProductProperties : undefined
        }];
        
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        
        if (!addResponse.ok) {
          throw new Error(`Failed to add items: ${addResponse.status}`);
        }
        
        const addResult = await addResponse.json();
        if (addResult.status) {
          throw new Error(addResult.description || 'Failed to add items to cart');
        }
        
        // Clear cache ONLY for the current product
        this.cache.clearCache(currentProductId);
        
        document.dispatchEvent(new CustomEvent('addon:set-added', { 
          detail: { mainProductIds: [currentProductId] }
        }));
        
        this.updateButtonText();
        
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          cartDrawer.refresh(false, 'addon-system');
        }
        
        if (cartDrawer) {
          setTimeout(() => {
            cartDrawer.open();
          }, 300);
        }
        
      } catch (error) {
        console.error('Error adding set to basket:', error);
      } finally {
        if (mainProductButton) {
          this.setMainProductButtonLoading(mainProductButton, false);
        }
      }
    }
    
    setMainProductButtonLoading(button, loading) {
      if (!button) return;
      
      const spinner = button.querySelector('.loading__spinner');
      
      if (loading) {
        button.disabled = true;
        if (spinner) {
          spinner.classList.remove('hidden');
        }
      } else {
        button.disabled = false;
        if (spinner) {
          spinner.classList.add('hidden');
        }
      }
    }
  }
  
  window.AddonSharedServices = new AddonSharedServices();
}
